import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * Compact luxury dropdown for PDP variations.
 *
 * Props
 * -----
 * - label         : field label ("Size", "Colour"…)
 * - placeholder   : "Select Size" text when nothing chosen
 * - value         : currently selected option value (string)
 * - options       : Array of { value, label?, addon?, hex?, disabled? }
 *                    - label optional (defaults to value)
 *                    - addon (number) shows "+ formatted price" right-side
 *                    - hex (colour swatch) shows a circular swatch on the left
 * - onChange      : (value) => void
 * - formatPrice   : (n) => string  (e.g. "$17.99"), used for `addon`
 * - testid        : stable data-testid prefix
 * - trailing      : optional right-column element (e.g. "Size chart ↗" button)
 *
 * Renders as a full-width button with the current selection, a chevron and an
 * optional swatch. The list flips above the trigger when the viewport bottom is
 * too close (auto-flip). Fully touch, keyboard, and mouse accessible.
 */
export const PillSelect = ({
  label,
  placeholder = "Select…",
  value,
  options = [],
  onChange,
  formatPrice,
  testid = "pill-select",
  trailing = null,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => (o.value ?? o) === value),
    [options, value]
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    // Auto-flip when there's not enough room below
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setFlipUp(window.innerHeight - rect.bottom < 260);
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const fmt = (n) => (formatPrice ? formatPrice(n) : `₹${n}`);
  const displayLabel = selected?.label ?? selected?.value ?? "";
  const displayAddon = selected?.addon > 0 ? ` (+${fmt(selected.addon)})` : "";

  return (
    <div className="relative w-full">
      {label && (
        <div className="overline text-forest/60 mb-2 flex items-center justify-between gap-2">
          <span className="tracking-[2px] text-[10px]">{label}</span>
          {trailing}
        </div>
      )}
      <button
        type="button"
        ref={btnRef}
        onClick={() => !disabled && setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        data-testid={`${testid}-trigger`}
        className={`w-full flex items-center gap-3 border rounded-lg bg-white px-3.5 py-3 text-left transition-colors ${
          open ? "border-forest" : "border-hairline hover:border-forest/50"
        } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {selected?.hex && (
          <span
            aria-hidden
            className="h-5 w-5 rounded-full border border-hairline shrink-0"
            style={{ background: selected.hex }}
          />
        )}
        <span className={`flex-1 min-w-0 truncate text-sm ${selected ? "text-forest" : "text-forest/45"}`}>
          {selected ? `${displayLabel}${displayAddon}` : placeholder}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={1.8}
          className={`text-forest/60 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          data-testid={`${testid}-menu`}
          className={`absolute z-40 left-0 right-0 max-h-64 overflow-y-auto bg-white border border-forest/20 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.15)] rounded-lg py-1 ${
            flipUp ? "bottom-[calc(100%+4px)]" : "top-[calc(100%+4px)]"
          }`}
        >
          {options.map((raw) => {
            const opt = typeof raw === "string" ? { value: raw } : raw;
            const on = value === opt.value;
            const oLabel = opt.label ?? opt.value;
            return (
              <li key={String(opt.value)}>
                <button
                  type="button"
                  onClick={() => { if (opt.disabled) return; onChange(opt.value); setOpen(false); }}
                  disabled={opt.disabled}
                  aria-selected={on}
                  role="option"
                  data-testid={`${testid}-opt-${opt.value}`}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-left transition-colors ${
                    opt.disabled ? "opacity-40 cursor-not-allowed" : on ? "bg-bone/60 text-forest" : "text-forest hover:bg-bone/40"
                  }`}
                >
                  {opt.hex && (
                    <span
                      aria-hidden
                      className="h-5 w-5 rounded-full border border-hairline shrink-0"
                      style={{ background: opt.hex }}
                    />
                  )}
                  <span className="flex-1 min-w-0 truncate">{oLabel}</span>
                  {opt.addon > 0 && (
                    <span className="tabnum text-gold text-xs shrink-0">+ {fmt(opt.addon)}</span>
                  )}
                  {on && <Check size={13} strokeWidth={2} className="text-forest shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PillSelect;
